import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '@environment';
import Bowser from 'bowser';

/** Campos que son columna en `app_security.device`; viajan como cabeceras sueltas. */
export type DeviceHeaders = {
  'X-OS-Name'?: string;
  'X-OS-Version'?: string;
  'X-Device-Manufacturer'?: string;
  'X-Device-Model'?: string;
  'X-Device-Type'?: string;
  'X-Device-Data'?: string;
};

/**
 * Datos del navegador para el backend. Las columnas van en cabeceras sueltas, legibles en logs sin
 * decodificar; la huella de bajo valor consultivo va en `X-Device-Data` (JSON en Base64).
 * El identificador viaja aparte en `X-Device-Id` (ver `DeviceIdService`).
 */
@Injectable({ providedIn: 'root' })
export class DeviceInfoService {
  // Los reemplaza el pipeline antes de compilar; en local traen los defaults de environment.ts.
  public readonly platform = 'WEB';
  public readonly appVersion: string = environment.appVersion;
  public readonly appBuild: string = environment.appBuild;

  private mHeaders: DeviceHeaders | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  /** Solo para las operaciones de autenticación, las únicas que escriben el dispositivo. */
  public getDeviceHeaders(): DeviceHeaders {
    if (this.mHeaders) return this.mHeaders;
    if (!isPlatformBrowser(this.platformId)) return {};

    this.mHeaders = this.build();
    return this.mHeaders;
  }

  private build(): DeviceHeaders {
    const parsed = Bowser.parse(navigator.userAgent);

    const metadata = {
      screenWidth: screen.width,
      screenHeight: screen.height,
      screenDensity: window.devicePixelRatio,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      locale: navigator.language,
      browserName: parsed.browser.name ?? null,
      browserVersion: parsed.browser.version ?? null,
    };
    const bytes = new TextEncoder().encode(JSON.stringify(metadata));

    const headers: DeviceHeaders = {
      'X-OS-Name': asciiSafe(parsed.os.name),
      // Windows reporta "NT 10.0"; sin el prefijo todas las versiones quedan numéricas.
      'X-OS-Version': asciiSafe(parsed.os.version?.replace(/^NT /, '')),
      // Marca y modelo solo los expone el user-agent en navegadores móviles.
      'X-Device-Manufacturer': asciiSafe(parsed.platform.vendor),
      'X-Device-Model': asciiSafe(parsed.platform.model),
      'X-Device-Type': asciiSafe(parsed.platform.type),
      'X-Device-Data': btoa(String.fromCharCode(...bytes)),
    };

    // Una cabecera vacía es ruido que el backend descarta igual.
    return Object.fromEntries(Object.entries(headers).filter(([, value]) => value)) as DeviceHeaders;
  }
}

/** Las cabeceras HTTP son ISO-8859-1: un valor fuera de ese rango rompe la request. */
function asciiSafe(value: string | undefined): string {
  if (!value) return '';
  return value
    .split('')
    .filter((char) => char.charCodeAt(0) >= 0x20 && char.charCodeAt(0) <= 0x7e)
    .join('')
    .trim();
}
